import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { AppStore, AppState } from '@/types/store'
import type { BankAccount } from '@/services/userData'

const initialState: AppState = {
  backendCache: {
    plaid: false,
    google: false,
    bankAccounts: [] as BankAccount[],
    ready: false,
    userId: null,
  },
  connections: {
    plaid: {
      connected: false,
      linkToken: null,
      expiry: 0,
      isLoading: false,
    },
    google: {
      connected: false,
      expiry: 0,
      userSpreadsheets: [],
    },
  },
  form: {
    sheetSelection: 'template',
    spreadsheetTitle: '',
    existingSpreadsheetId: '',
    existingSheetTab: '',
    exportType: 'monthly',
    startDate: '',
    endDate: '',
    syncFrequency: 'Weekly',
    enableAutomaticSyncing: false,
    selectedAccountIds: [],
  },
  sync: {
    isLoading: false,
    spreadsheetUrl: null,
    spreadsheetId: null,
    lastSyncDate: null,
  },
  toast: {
    message: '',
    type: 'info',
  },
}

export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Plaid actions
      setPlaidConnected: (connected) =>
        set((state) => ({
          connections: {
            ...state.connections,
            plaid: { ...state.connections.plaid, connected },
          },
        })),

      setPlaidLinkToken: (linkToken) =>
        set((state) => ({
          connections: {
            ...state.connections,
            plaid: { ...state.connections.plaid, linkToken },
          },
        })),

      setPlaidExpiry: (expiry) =>
        set((state) => ({
          connections: {
            ...state.connections,
            plaid: { ...state.connections.plaid, expiry },
          },
        })),

      setPlaidLoading: (isLoading) =>
        set((state) => ({
          connections: {
            ...state.connections,
            plaid: { ...state.connections.plaid, isLoading },
          },
        })),

      resetPlaid: () =>
        set((state) => ({
          connections: {
            ...state.connections,
            plaid: initialState.connections.plaid,
          },
        })),

      // Google actions
      setGoogleConnected: (connected) =>
        set((state) => ({
          connections: {
            ...state.connections,
            google: { ...state.connections.google, connected },
          },
        })),

      setGoogleExpiry: (expiry) =>
        set((state) => ({
          connections: {
            ...state.connections,
            google: { ...state.connections.google, expiry },
          },
        })),

      setUserSpreadsheets: (userSpreadsheets) =>
        set((state) => ({
          connections: {
            ...state.connections,
            google: {
              ...state.connections.google,
              userSpreadsheets: Array.isArray(userSpreadsheets) ? userSpreadsheets : [],
            },
          },
        })),

      resetGoogle: () =>
        set((state) => ({
          connections: {
            ...state.connections,
            google: initialState.connections.google,
          },
        })),

      // Form actions
      updateForm: (updates) =>
        set((state) => ({
          form: { ...state.form, ...updates },
        })),

      resetForm: () =>
        set(() => ({
          form: initialState.form,
        })),

      // Sync actions
      setSyncLoading: (isLoading) =>
        set((state) => ({
          sync: { ...state.sync, isLoading },
        })),

      setSpreadsheetUrl: (spreadsheetUrl) =>
        set((state) => ({
          sync: { ...state.sync, spreadsheetUrl },
        })),

      setSpreadsheetId: (spreadsheetId) =>
        set((state) => ({
          sync: { ...state.sync, spreadsheetId },
        })),

      setLastSyncDate: (lastSyncDate) =>
        set((state) => ({
          sync: { ...state.sync, lastSyncDate },
        })),

      resetSync: () =>
        set(() => ({
          sync: initialState.sync,
        })),

      // Toast actions
      showToast: (message, type = 'info') =>
        set(() => ({
          toast: { message, type },
        })),

      clearToast: () =>
        set(() => ({
          toast: initialState.toast,
        })),

      // Backend cache actions
      setBackendCache: ({ plaid, google, bankAccounts, userId }) =>
        set(() => ({
          backendCache: { plaid, google, bankAccounts, ready: true, userId },
        })),

      // Utility actions
      resetAll: () => set(initialState),
    }),
    {
      name: 'app-storage',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist certain parts of the state
      partialize: (state) => ({
        backendCache: state.backendCache,
        connections: state.connections,
        form: state.form,
        sync: {
          spreadsheetUrl: state.sync.spreadsheetUrl,
          spreadsheetId: state.sync.spreadsheetId,
          lastSyncDate: state.sync.lastSyncDate,
        },
      }),
    }
  )
)

// Selector hooks for better performance
export const usePlaidConnection = () => {
  const store = useAppStore()
  return {
    plaidConnected: store.connections.plaid.connected,
    linkToken: store.connections.plaid.linkToken,
    expiry: store.connections.plaid.expiry,
    isLoading: store.connections.plaid.isLoading,
    setPlaidConnected: store.setPlaidConnected,
    setPlaidLinkToken: store.setPlaidLinkToken,
    setPlaidExpiry: store.setPlaidExpiry,
    setPlaidLoading: store.setPlaidLoading,
    resetPlaid: store.resetPlaid,
  }
}

export const useGoogleConnection = () => {
  const store = useAppStore()
  return {
    googleConnected: store.connections.google.connected,
    expiry: store.connections.google.expiry,
    userSpreadsheets: store.connections.google.userSpreadsheets,
    setGoogleConnected: store.setGoogleConnected,
    setGoogleExpiry: store.setGoogleExpiry,
    setUserSpreadsheets: store.setUserSpreadsheets,
    resetGoogle: store.resetGoogle,
  }
}

export const useFormData = () => {
  const store = useAppStore()
  return {
    formData: store.form,
    updateForm: store.updateForm,
    resetForm: store.resetForm,
  }
}

export const useSyncState = () => {
  const store = useAppStore()
  return {
    isLoading: store.sync.isLoading,
    spreadsheetUrl: store.sync.spreadsheetUrl,
    spreadsheetId: store.sync.spreadsheetId,
    lastSyncDate: store.sync.lastSyncDate,
    setSyncLoading: store.setSyncLoading,
    setSpreadsheetUrl: store.setSpreadsheetUrl,
    setSpreadsheetId: store.setSpreadsheetId,
    setLastSyncDate: store.setLastSyncDate,
    resetSync: store.resetSync,
  }
}

export const useToast = () => {
  const store = useAppStore()
  return {
    toastMessage: store.toast.message,
    toastType: store.toast.type,
    showToast: store.showToast,
    clearToast: store.clearToast,
  }
}
